import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Users,
  Award,
  Shield,
  Clock
} from 'lucide-react'
import { ABOUT_STATS } from '../constants/stats'

const About = () => {

  const achievements = ABOUT_STATS.map(stat => ({
    number: stat.value,
    label: stat.label,
    description: stat.icon === 'Car' ? 'Успешно защитили от коррозии' :
                stat.icon === 'Shield' ? 'В сфере антикоррозийной защиты' :
                stat.icon === 'Star' ? 'По результатам опросов' :
                'Гарантированная защита'
  }))

  const values = [
    {
      icon: Shield,
      title: 'Качество',
      description: 'Мы не идем на компромиссы в вопросах качества. Каждая работа выполняется с максимальным вниманием к деталям.'
    },
    {
      icon: Clock,
      title: 'Надежность',
      description: 'Соблюдаем все сроки выполнения работ и предоставляем гарантии на все виды услуг.'
    },
    {
      icon: Users,
      title: 'Клиентоориентированность',
      description: 'Ставим интересы клиента на первое место и всегда готовы пойти навстречу.'
    },
    {
      icon: Award,
      title: 'Профессионализм',
      description: 'Постоянно совершенствуем навыки и используем только проверенные технологии.'
    }
  ]

  return (
    <div className="page-padding">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 text-white py-20">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
          >
            О нас
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed"
          >
            Мы - команда профессионалов, специализирующаяся на антикоррозийной
            защите автомобилей в Бресте с 2022 года
          </motion.p>
          <div className="mt-6">
            <a href="/anticor-brest" className="underline text-white font-semibold">
              Антикор Брест — услуги и цены
            </a>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-6">
                Наша история
              </h2>
              <div className="space-y-4 text-lg text-secondary-600 leading-relaxed">
                <p>
                  Компания "Антикор Авто" была основана в 2022 году группой
                  энтузиастов, которые поставили перед собой цель предоставить
                  жителям Бреста и области качественные услуги по антикоррозийной
                  защите автомобилей.
                </p>
                <p>
                  Наша миссия - защитить ваш автомобиль от коррозии, используя
                  проверенные технологии и качественные материалы, чтобы вы могли
                  наслаждаться поездками без беспокойства о состоянии кузова.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8"
            >
              <div className="text-center">
                <div className="w-32 h-32 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white text-4xl font-bold">2022</span>
                </div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  Год основания
                </h3>
                <p className="text-secondary-600">
                  С этого года мы начали защищать автомобили
                  жителей Бреста от коррозии
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-secondary-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Наши ценности
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Мы руководствуемся принципами, которые помогают нам
              предоставлять качественные услуги и развиваться
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6">
                  <value.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-secondary-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Наши достижения
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              За годы работы мы достигли значительных результатов
              и заслужили доверие тысяч клиентов
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-primary-600 mb-2">
                  {achievement.number}
                </div>
                <div className="text-lg font-semibold text-secondary-900 mb-2">
                  {achievement.label}
                </div>
                <p className="text-secondary-600 text-sm">
                  {achievement.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-accent-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Доверьте свой автомобиль профессионалам
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Наша команда готова защитить ваш автомобиль от коррозии
              с использованием лучших технологий и материалов
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-accent">
                Записаться на обработку
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default About
